import React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, DollarSign, Percent } from "lucide-react";

export default function CommissionDialog({ open, onClose, creator, onCreated }) {
    const [form, setForm] = React.useState({
        title: "",
        description: "",
        budget: "",
        deadline: "",
        payment_type: "flat",
        rev_share_pct: "",
        references: "",
    });
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (!open) {
            setForm({
                title: "",
                description: "",
                budget: "",
                deadline: "",
                payment_type: "flat",
                rev_share_pct: "",
                references: "",
            });
        }
    }, [open]);

    if (!creator) return null;

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                creator_id: creator.id,
                title: form.title,
                description: form.description,
                budget: parseFloat(form.budget) || 0,
                deadline: form.deadline,
                payment_type: form.payment_type,
                rev_share_pct: parseFloat(form.rev_share_pct) || 0,
                references: form.references,
            };
            const { data } = await api.post("/commissions", payload);
            toast.success(`Brief sent to ${creator.name}`);
            onCreated?.(data);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-xl bg-white border-slate-200 rounded-2xl"
                data-testid="commission-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-slate-900 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-coral" />
                        Commission {creator.name}
                    </DialogTitle>
                    <DialogDescription className="text-slate-500 text-sm">
                        Send {creator.name} a brief for a custom video. They&apos;ll accept or
                        decline. Once delivered, you can pay and connect to ads.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2" data-testid="commission-form">
                    <div className="space-y-2">
                        <Label className="overline text-slate-600">Brief title</Label>
                        <Input
                            required
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="30-sec UGC for our new sneaker"
                            className="rounded-xl border-slate-200 h-11 focus-visible:ring-coral focus-visible:ring-2"
                            data-testid="commission-title"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="overline text-slate-600">Description / brief</Label>
                        <Textarea
                            required
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="What should the creator film? Tone, style, must-haves…"
                            rows={4}
                            className="rounded-xl border-slate-200 focus-visible:ring-coral focus-visible:ring-2"
                            data-testid="commission-description"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="overline text-slate-600">Reference links (optional)</Label>
                        <Input
                            value={form.references}
                            onChange={(e) => setForm({ ...form, references: e.target.value })}
                            placeholder="Drive / Notion / inspiration links"
                            className="rounded-xl border-slate-200 h-11 focus-visible:ring-coral focus-visible:ring-2"
                            data-testid="commission-references"
                        />
                    </div>
                    <div>
                        <Label className="overline text-slate-600 mb-2 block">Payment</Label>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            {[
                                { v: "flat", label: "Flat fee", icon: DollarSign },
                                { v: "rev_share", label: "Rev share", icon: Percent },
                            ].map((p) => (
                                <button
                                    key={p.v}
                                    type="button"
                                    onClick={() => setForm({ ...form, payment_type: p.v })}
                                    className={`p-3 rounded-xl border text-left transition-all ${
                                        form.payment_type === p.v
                                            ? "border-coral bg-coral/5 text-slate-900"
                                            : "border-slate-200 hover:border-slate-300 text-slate-600"
                                    }`}
                                    data-testid={`payment-${p.v}`}
                                >
                                    <p.icon className="w-4 h-4 mb-1 text-coral" />
                                    <div className="text-sm font-semibold">{p.label}</div>
                                </button>
                            ))}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs text-slate-500">Budget USD</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={form.budget}
                                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                                    className="rounded-xl border-slate-200 h-11 mt-1 focus-visible:ring-coral focus-visible:ring-2"
                                    data-testid="commission-budget"
                                />
                            </div>
                            {form.payment_type === "rev_share" && (
                                <div>
                                    <Label className="text-xs text-slate-500">Rev share %</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={form.rev_share_pct}
                                        onChange={(e) =>
                                            setForm({ ...form, rev_share_pct: e.target.value })
                                        }
                                        className="rounded-xl border-slate-200 h-11 mt-1 focus-visible:ring-coral focus-visible:ring-2"
                                        data-testid="commission-rev-share"
                                    />
                                </div>
                            )}
                            <div className={form.payment_type === "rev_share" ? "col-span-2" : ""}>
                                <Label className="text-xs text-slate-500">Deadline</Label>
                                <Input
                                    type="date"
                                    value={form.deadline}
                                    onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                                    className="rounded-xl border-slate-200 h-11 mt-1 focus-visible:ring-coral focus-visible:ring-2"
                                    data-testid="commission-deadline"
                                />
                            </div>
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-pill-primary w-full"
                        data-testid="commission-submit"
                    >
                        {submitting ? "Sending…" : "Send brief →"}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
